import type { ContractStatus } from '@weft/types';
import { CONTRACT_STATUSES } from '@weft/types';
import { dayjs, raiseUnreachable } from '@weft/utils';
import cn from 'classnames';
import type { HTMLAttributes } from 'react';
import React, { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { formatDate } from '../../utils/date';

type ContractStatusChipProps = HTMLAttributes<HTMLSpanElement> & {
  status: ContractStatus;
  endDate?: Date;
};

export const ContractStatusChip = forwardRef<HTMLSpanElement, ContractStatusChipProps>(
  ({ status, className, endDate, ...props }, ref) => {
    const isTerminationState = status === CONTRACT_STATUSES.Terminated || status === CONTRACT_STATUSES.PendingTermination;
    const defaultDate = dayjs('YYYY-MM-DD');

    return (
      <span
        className={twMerge(
          cn('font-medium px-3 py-2 rounded-[100px] w-fit',
            { 'text-active bg-active': status === CONTRACT_STATUSES.Active },
            { 'text-error bg-error': status === CONTRACT_STATUSES.Error },
            { 'text-[#939393] bg-[#ECECEC]': status === CONTRACT_STATUSES.Inactive },
            { 'text-[#D4A342] bg-[#FFF3D6]': isTerminationState }),
            
          className,
        )}
        ref={ref}
        {...props}
      >
        {getContractStatusLabel(status)}
        {isTerminationState && ` - ${formatDate(endDate ?? defaultDate)}`}
      </span>
    );
  },
);

export function getContractStatusLabel(contractStatus: ContractStatus) {
  switch (contractStatus) {
    case CONTRACT_STATUSES.Active:
      return 'Active';
    case CONTRACT_STATUSES.Inactive:
      return 'Inactive';
    case CONTRACT_STATUSES.Terminated:
      return 'Termination';
    case CONTRACT_STATUSES.PendingTermination:
      return 'Pending Termination';
    case CONTRACT_STATUSES.Error:
      return 'Error';
    default:
      return raiseUnreachable(contractStatus);
  }
}